import { useState, useEffect, useCallback } from "react"
import { FileText, Loader2 } from "lucide-react"
import type { DivergenceFile } from "../../types"
import { useSignedUrlMutation } from "@/services/file"

interface FileThumbnailProps {
  file: DivergenceFile
  onClick: () => void
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']

export const FileThumbnail = ({ file, onClick }: FileThumbnailProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isImage, setIsImage] = useState(false)
  const [signedUrl] = useSignedUrlMutation()

  useEffect(() => {
    loadImageThumbnail()
  }, [file])

  const loadImageThumbnail = useCallback(async () => {
    const isImageFile = IMAGE_TYPES.includes(file.file_type)
    setIsImage(isImageFile)

    if (!isImageFile) return

    setIsLoading(true)
    try {
      const response = await signedUrl({
        id: file.id,
        ResponseContentDisposition: "inline",
      }).unwrap()

      if (response?.url) {
        setThumbnailUrl(response.url)
      }
    } catch (error) {
      console.error('Erro ao carregar thumbnail:', error)
    } finally {
      setIsLoading(false)
    }
  }, [signedUrl, file])

  const handleImageError = () => {
    setIsImage(false)
    setThumbnailUrl(null)
  }

  const renderThumbnailContent = () => {
    if (isImage && isLoading) {
      return <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    }

    if (isImage && thumbnailUrl) {
      return (
        <img
          src={thumbnailUrl}
          alt={file.file_name}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      )
    }

    return <FileText className="w-8 h-8 text-gray-400" />
  }

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 rounded-lg border p-2 hover:bg-gray-50 transition-colors"
    >
      <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded overflow-hidden">
        {renderThumbnailContent()}
      </div>
      <p className="text-xs mt-1 text-center truncate max-w-[80px]">
        {file.file_name}
      </p>
    </button>
  )
}
